/**
 * CloudRenderer — TSL raymarch node for volumetric clouds.
 *
 * Produces a (rgb, alpha) output where `rgb` is in-scattered radiance and
 * `alpha` is 1 - transmittance (i.e. cloud opacity). Composite over the
 * existing scene color via `final = scene * (1 - alpha) + rgb`.
 *
 * ── THE ANCHORING / EVOLUTION MODEL (field-tested 2026-08-18) ──────────────
 * The sampled pattern position for a march point p is
 *
 *   patternXZ = rd·t  +  camAbs·parallaxScale  +  wind  (± evolve)
 *
 * three regimes, selected by `parallaxScale`:
 *   0        — pure camera rendering space (Unity HDRP "Rendering Space:
 *              Camera", the classic skybox convention): the sky NEVER slides
 *              against the world as the player moves.
 *   ~0.1-0.2 — TRAVEL EVOLUTION (tried 2026-08-18, reverted 2026-08-19):
 *              imperceptible per step (0.15 ⇒ a 100 m walk shifts the
 *              pattern 15 m ≈ 0.2° on a 4 km deck) — but at walking scale
 *              that is perceptually IDENTICAL to camera-glued, and the
 *              "clouds follow my camera" report persisted. Kept as a knob.
 *   1        — physically-correct world anchoring (full parallax), the
 *              shipped atlas default: clouds are STATIONARY in the world.
 *              Walk-speed response is physically small on a high deck
 *              (moon-follows-the-car); the LOW layer (~4° per 50 m walked)
 *              makes the anchoring legible. k>1 exaggerates — field-proven
 *              to swim against architecture; do not ship.
 * Rotation anchoring, wind, the sun cone, and vertical motion (flying up to
 * the layer) are correct in every regime; camAbs is rebase-continuous.
 *
 * Additionally the SHAPES morph in place over time via net-zero counter-
 * drift: the base noise drifts +evolveOffset while the detail-erosion noise
 * drifts −evolveOffset, so their interference pattern changes continuously
 * with zero average translation (Spiri0's "drift the frequencies at
 * different speeds with net 0 drift to get cloud evolution",
 * https://discourse.threejs.org/t/efficient-volumetric-clouds/66067).
 *
 * Algorithm (per fragment):
 *   1. Reconstruct the view ray in world space from `screenUV`.
 *   2. Intersect with the cloud layer (slab between bottomAlt and topAlt).
 *   3. Ray-march the segment with blue-noise-jittered, distance-grown
 *      adaptive steps: a larger `stepOutside` when density is zero
 *      (empty-space skipping) and a smaller `stepInside` inside a cloud —
 *      the same family as the log-spaced sampling in the efficient-clouds
 *      reference, and the adaptive 16..128-step march + blue-noise dither +
 *      depth-aware occlusion of the game-ready reference
 *      (https://discourse.threejs.org/t/volumetric-clouds-game-ready/86598).
 *   4. At each non-zero density sample, take 4 cone samples toward the
 *      sun and accumulate sun-visibility optical depth.
 *   5. Apply dual-lobe Henyey-Greenstein phase + Beer-Powder energy model.
 *
 * The density function is a simplified Schneider variant:
 *   base = remap(baseNoise.r, mid_octaves - 1, 1, 0, 1)
 *   d    = base * cumulusHeightGradient(h) * coverage - (1 - coverage)
 *   d    = max(0, d) * densityScale
 *   inside-cloud detail erosion uses detailNoise.
 *
 * Deliberately NOT adopted from the references (documented v2): temporal
 * reprojection (the efficient-clouds author abandoned TAA on clouds —
 * "cloud surfaces are inherently fuzzy" — matching our own afterAA lesson)
 * and a half-res cloud RT + bilateral upsample (needs a second pipeline
 * pass; current full-res march holds 60 fps at 'high').
 */

import type * as THREE from 'three/webgpu';
import {
  Fn, If, Loop, vec2, vec3, vec4, float, uniform, uint,
  exp, max, min, clamp, dot, mix, step, fract, sin,
  pow, normalize, texture3D, texture,
  screenUV, screenCoordinate,
} from 'three/tsl';
import { Vector3, Matrix4 } from 'three/webgpu';
import type { CloudConfig } from './CloudConfig.js';
import { getBlueNoiseTexture } from '../bluenoise/BlueNoiseRuntime.js';

export interface CloudRendererOptions {
  config: CloudConfig;
  baseNoise: THREE.Data3DTexture;
  detailNoise: THREE.Data3DTexture;
  weatherMap: THREE.DataTexture;
}

/**
 * Uniform bag — the caller pushes per-frame updates via `.value = ...`.
 */
export interface CloudUniforms {
  sunDirection: ReturnType<typeof uniform>;
  sunColor: ReturnType<typeof uniform>;
  windOffset: ReturnType<typeof uniform>;
  /**
   * Origin-rebase compensation (world-space metres, XZ). Large-world games
   * shift `scene.position` to keep render coordinates small; the cloud noise
   * field samples RENDER-space positions, so without compensation the entire
   * cloudscape re-arranges at every rebase snap. CloudRuntimeSystem feeds
   * `scene.position.(x,z)` here each frame; the shader subtracts it to
   * recover ABSOLUTE world XZ for the noise/weather UVs.
   */
  worldOffset: ReturnType<typeof uniform>;
  /**
   * Explicit render-camera state (position / world matrix / inverse
   * projection), fed per frame by CloudRuntimeSystem. WHY: the composite node
   * evaluates inside a POST-PROCESSING QUAD render, where the ambient TSL
   * accessors (`cameraPosition`, `cameraWorldMatrix`, …) bind inconsistently —
   * rotation followed the scene camera but the position stuck at the quad
   * camera's origin, so the cloudscape NEVER parallaxed with movement (it
   * behaved as a view-glued skybox, defeating the whole depth-aware-pass
   * design). Explicit uniforms are unambiguous in every pass context.
   */
  cameraPos: ReturnType<typeof uniform>;
  cameraWorldMat: ReturnType<typeof uniform>;
  cameraProjInvMat: ReturnType<typeof uniform>;
  coverage: ReturnType<typeof uniform>;
  extinction: ReturnType<typeof uniform>;
  densityScale: ReturnType<typeof uniform>;
  bottomAltitude: ReturnType<typeof uniform>;
  topAltitude: ReturnType<typeof uniform>;
  sunIlluminance: ReturnType<typeof uniform>;
  ambientBottom: ReturnType<typeof uniform>;
  ambientTop: ReturnType<typeof uniform>;
  phaseForwardG: ReturnType<typeof uniform>;
  phaseBackwardG: ReturnType<typeof uniform>;
  powderStrength: ReturnType<typeof uniform>;
  noiseScale: ReturnType<typeof uniform>;
  /** Camera-translation response (see CloudConfig.parallaxScale). */
  parallaxScale: ReturnType<typeof uniform>;
  /**
   * Net-zero shape-evolution offset (world-space metres, XZ), accumulated by
   * `tick()` at `config.evolveSpeed`. Applied +offset to the base noise and
   * −offset to the detail-erosion noise so the shapes morph in place with
   * zero average drift.
   */
  evolveOffset: ReturnType<typeof uniform>;
}

export class CloudRenderer {
  readonly uniforms: CloudUniforms;
  readonly config: CloudConfig;
  /** Wind drift (world metres) the weather map is sampled with — cloud shadows read the same drift (stage 4). */
  get windOffset(): THREE.Vector2 { return this.uniforms.windOffset.value as THREE.Vector2; }
  private readonly _baseNoise: THREE.Data3DTexture;
  private readonly _detailNoise: THREE.Data3DTexture;
  private readonly _weatherMap: THREE.DataTexture;
  private _node: unknown | null = null;
  private _elapsedSeconds = 0;

  constructor(opts: CloudRendererOptions) {
    this.config = opts.config;
    this._baseNoise = opts.baseNoise;
    this._detailNoise = opts.detailNoise;
    this._weatherMap = opts.weatherMap;

    this.uniforms = {
      sunDirection: uniform(vec3(0, 1, 0)),
      sunColor: uniform(vec3(1, 1, 1)),
      windOffset: uniform(vec2(0, 0)),
      worldOffset: uniform(vec2(0, 0)),
      cameraPos: uniform(new Vector3(0, 0, 0)),
      cameraWorldMat: uniform(new Matrix4()),
      cameraProjInvMat: uniform(new Matrix4()),
      coverage: uniform(this.config.coverage),
      extinction: uniform(this.config.extinction),
      densityScale: uniform(this.config.densityScale),
      bottomAltitude: uniform(this.config.bottomAltitude),
      topAltitude: uniform(this.config.topAltitude),
      sunIlluminance: uniform(this.config.sunIlluminance),
      ambientBottom: uniform(this.config.ambientBottom),
      ambientTop: uniform(this.config.ambientTop),
      phaseForwardG: uniform(this.config.phaseForwardG),
      phaseBackwardG: uniform(this.config.phaseBackwardG),
      powderStrength: uniform(this.config.powderStrength),
      noiseScale: uniform(this.config.noiseScale),
      parallaxScale: uniform(this.config.parallaxScale),
      evolveOffset: uniform(vec2(0, 0)),
    };
  }

  /** Advance the wind + shape-evolution offsets (called per-frame). */
  tick(dt: number): void {
    // windTimeScale: physically-scaled wind reads as a frozen sky from the
    // ground (see CloudConfig) — scale TIME, not the vector, so a runtime
    // change speeds the drift without teleporting the pattern.
    this._elapsedSeconds += dt * (this.config.windTimeScale ?? 1);
    const wx = this.config.windX * this._elapsedSeconds;
    const wz = this.config.windZ * this._elapsedSeconds;
    (this.uniforms.windOffset.value as THREE.Vector2).set(wx, wz);
    // Net-zero shape evolution: a fixed off-axis direction (incommensurate
    // with the wind) so morphing never reads as a second wind layer.
    const ev = this.config.evolveSpeed * this._elapsedSeconds;
    (this.uniforms.evolveOffset.value as THREE.Vector2).set(ev * 0.62, ev * -0.79);
  }

  setSunDirection(dir: { x: number; y: number; z: number }): void {
    (this.uniforms.sunDirection.value as THREE.Vector3).set(dir.x, dir.y, dir.z);
  }

  /**
   * Push the render camera's state into the explicit camera uniforms.
   * Call once per frame (CloudRuntimeSystem does) — see CloudUniforms.cameraPos
   * for why the ambient TSL camera accessors cannot be used here.
   */
  setCameraState(camera: THREE.Camera): void {
    (this.uniforms.cameraPos.value as THREE.Vector3).setFromMatrixPosition(camera.matrixWorld);
    (this.uniforms.cameraWorldMat.value as THREE.Matrix4).copy(camera.matrixWorld);
    (this.uniforms.cameraProjInvMat.value as THREE.Matrix4).copy(camera.projectionMatrixInverse);
  }

  setSunColor(col: { r: number; g: number; b: number }): void {
    (this.uniforms.sunColor.value as THREE.Vector3).set(col.r, col.g, col.b);
  }

  setCoverage(v: number): void {
    this.uniforms.coverage.value = Math.max(0, Math.min(1, v));
  }

  /**
   * Camera-translation response (live uniform). 1 = physically correct
   * world anchoring; >1 exaggerates; **0 = camera rendering space** (HDRP
   * "Rendering Space: Camera" / classic skybox convention): the pattern
   * becomes a pure function of view direction + wind, so the sky NEVER
   * slides against the world when the player moves.
   */
  setParallaxScale(v: number): void {
    this.uniforms.parallaxScale.value = Math.max(0, Math.min(8, v));
  }

  setDensityScale(v: number): void {
    this.uniforms.densityScale.value = Math.max(0, v);
  }

  /**
   * Build the TSL node that evaluates cloud radiance + opacity for a given
   * screen UV. Intended to composite over an existing scene color buffer.
   *
   * Returns an RGBA node: .rgb = in-scattered radiance, .a = opacity.
   */
  buildCompositeNode(): unknown {
    if (this._node) return this._node;

    const u = this.uniforms;
    const steps = this.config.quality.primarySamples;
    const sunSteps = this.config.quality.sunSamples;
    const dtIn = this.config.quality.stepInside;
    const dtOut = this.config.quality.stepOutside;
    const weatherScale = this.config.weatherScale;

    const baseNoiseTex = this._baseNoise;
    const detailNoiseTex = this._detailNoise;
    const weatherTex = this._weatherMap;

    // TSL helpers as PLAIN functions (NOT Fn-wrapped): they build their node graph
    // INLINE at each call site. The previous `Fn(([arg]) => …)` form — called NESTED
    // (marchFn → sunCone → sampleDensity, marchFn → dualHG → hg) — failed WGSL codegen:
    // the inline-Fn parameter substitution dropped the args, so `p`/`cosTheta`/`g`
    // resolved to a non-node and `p.y.sub(…)` threw "Cannot read properties of undefined
    // (reading 'sub')", the cloud layer failed to build, and the sky rendered WITHOUT
    // clouds. Inlining removes the parameter binding (same fix as src/particles/gpu/
    // kernels.ts). Do NOT re-wrap these in `Fn` without an explicit type layout.
    const sampleDensity = (p: any): any => {
      // Height fraction 0..1 across the cloud layer.
      const hAbs = p.y.sub(u.bottomAltitude);
      const hSpan = u.topAltitude.sub(u.bottomAltitude);
      const h = clamp(hAbs.div(hSpan), float(0), float(1));

      // Absolute world XZ: undo the origin rebase (worldOffset = scene.position.xz),
      // then scroll with wind. Without this the cloudscape re-arranges on rebase.
      // parallaxScale > 1 adds (k−1)·camAbs — a rigid pattern translation that
      // makes walking d metres read as k·d of travel under the clouds (see
      // CloudConfig.parallaxScale). camAbs is continuous across rebase snaps
      // (cameraPos and worldOffset change together), so no pattern jump.
      const camAbsXZ = u.cameraPos.xz.sub(u.worldOffset);
      const pAbsXZ = p.xz.sub(u.worldOffset)
        .add(camAbsXZ.mul(u.parallaxScale.sub(float(1))));

      // Tile de-phasing (live screenshots 2026-07-31): the 64³ base noise
      // wraps every 1/noiseScale (~2.5 km) and a horizon path crosses ~11
      // aligned repeats — identical worley cells lined up into perspective
      // ROWS of puffs. Two incommensurate large-period sine warps (~18/27 km)
      // shift adjacent repeats by ~0.9 rad of warp phase so no two repeats
      // sample the same cells. Periods are kept ≥18 km so the local domain
      // gradient stays ≤ ~0.31 (the first cut used 9/13 km ⇒ ±62% shear that
      // visibly smeared the worley puffs into streaks).
      const warpX = sin(pAbsXZ.x.mul(0.00035).add(pAbsXZ.y.mul(0.000185))).mul(880);
      const warpZ = sin(pAbsXZ.y.mul(0.000235).sub(pAbsXZ.x.mul(0.000145))).mul(880);

      // Anti-grid weather sampling. The weather map repeats every
      // 1/weatherScale (10 km) SQUARE-ON to the world axes, and 20-40 km of
      // horizon visibility shows several identical repeats — the sky read as
      // a grid of stamped masses. Three decorrelators, one extra 2D fetch:
      //  1. ROTATE the domain ~27° off the world axes (a tilted lattice
      //     stops registering as a grid, and never parallels city streets);
      //  2. reuse the sine WARPS (±880 m ≈ ±9% tile, incommensurate periods)
      //     so adjacent repeats also de-phase;
      //  3. multiply coverage by a MACRO envelope — the SAME texture sampled
      //     ~2.65× coarser and counter-rotated, so each 10 km repeat sits
      //     under a different part of a ~27 km field and no two repeats
      //     render alike (combined period is effectively unbounded).
      const wwx = pAbsXZ.x.add(u.windOffset.x).add(warpX);
      const wwz = pAbsXZ.y.add(u.windOffset.y).add(warpZ);
      const weatherUV = vec2(
        wwx.mul(0.891).sub(wwz.mul(0.454)),
        wwx.mul(0.454).add(wwz.mul(0.891)),
      ).mul(weatherScale);
      const weather = texture(weatherTex, weatherUV);
      const macroUV = vec2(
        wwx.mul(0.968).add(wwz.mul(0.25)),
        wwz.mul(0.968).sub(wwx.mul(0.25)),
      ).mul(weatherScale * 0.377).add(vec2(0.17, 0.61));
      const macro = texture(weatherTex, macroUV).r;
      const rEff = clamp(weather.r.mul(float(0.55).add(macro.mul(1.2))), float(0), float(1));
      // Local coverage = global coverage knob MODULATED by the weather field,
      // so the map can carve genuine CLEAR sky (gaps) instead of only ever
      // ADDING cloud. The previous `max(coverage, weather.r)` floored local
      // coverage at the larger of the two ⇒ density never thinned below the
      // global knob ⇒ a uniform overcast band with no holes.
      //   - weather.r ≈ 1 (dense mass) → cov ≈ 2·coverage (capped) — full cloud
      //   - weather.r ≈ 0.5 (mean)     → cov ≈ coverage          — partly cloudy
      //   - weather.r ≈ 0 (clear)      → cov ≈ 0                  — open sky
      // The ×2 keeps a coverage=0.4 knob reaching ~0.8 under the densest masses
      // (believable cumulus) while still opening to clear sky between them.
      // Cluster/size octave (weather.b): regionally scales coverage so the
      // sky breaks into fields of many small puffs vs a few big massed
      // decks — without it every mass renders at one repeated size.
      const cov = clamp(
        u.coverage.mul(rEff).mul(float(0.6).add(weather.b.mul(0.8))).mul(float(2)),
        float(0), float(1),
      );

      // Cloud TYPE (weather.g, Nubis-style): compress the height domain so a
      // low-type mass tops out at ~45% of the slab (flat stratocumulus deck)
      // while a high-type mass fills it (tall cumulus tower). hn > 1 clamps
      // and the bell's (1-hn) term zeroes density above each cloud's own
      // ceiling — neighbouring clouds get genuinely different heights.
      const hMax = mix(float(0.45), float(1.0), weather.g);
      const hn = clamp(h.div(hMax), float(0), float(1));
      const hGrad = float(4).mul(hn).mul(float(1).sub(hn));

      // Base noise UVW — pattern-space + wind scroll + POSITIVE half of the
      // net-zero evolution drift (the detail noise takes the negative half,
      // so the eroded silhouette morphs in place — see the header).
      const baseUVW = vec3(
        pAbsXZ.x.add(u.windOffset.x).add(warpX).add(u.evolveOffset.x).mul(u.noiseScale),
        p.y.mul(u.noiseScale),
        pAbsXZ.y.add(u.windOffset.y).add(warpZ).add(u.evolveOffset.y).mul(u.noiseScale),
      );
      const baseSample = texture3D(baseNoiseTex, baseUVW);

      // Combine mid octaves (Schneider's "FBM" step — GBA channels weighted).
      const midFBM = baseSample.g.mul(0.625)
        .add(baseSample.b.mul(0.25))
        .add(baseSample.a.mul(0.125));

      // "Dilate" remap — sub mid from base, then remap to [0, 1].
      const dilated = clamp(
        baseSample.r.sub(midFBM.sub(1).mul(0.5)),
        float(0), float(1),
      );

      // Coverage-gated density — Schneider's remap form:
      //   remap(shape, 1 − cov, 1, 0, 1) · cov
      // WHY (the "invisible clouds" bug): the old subtract form
      // `shape·cov − (1 − cov)` needed `shape ≥ (1−cov)/cov` — with the real
      // weather-map range that was negative almost everywhere and the layer
      // composited at ~0 opacity. The remap keeps the same clear-sky gate
      // (density is 0 until shape exceeds 1 − cov) but rescales the surviving
      // range to [0, 1] so masses actually reach cloud density; the final
      // ·cov thins sparse-coverage areas instead of erasing them.
      const shaped = dilated.mul(hGrad);
      const gate = float(1).sub(cov);
      const preErode = clamp(
        shaped.sub(gate).div(max(cov, float(1e-4))),
        float(0), float(1),
      ).mul(cov);

      // Detail erosion — only applied when preErode > 0. Counter-drifts the
      // evolution offset (−2× undoes the base's +1× inside the shared UVW,
      // netting −1×) so base∧detail interference morphs with zero average
      // translation.
      const detailUVW = baseUVW.sub(vec3(
        u.evolveOffset.x.mul(2).mul(u.noiseScale),
        float(0),
        u.evolveOffset.y.mul(2).mul(u.noiseScale),
      )).mul(4);
      const detailSample = texture3D(detailNoiseTex, detailUVW);
      const detailFBM = detailSample.r.mul(0.625)
        .add(detailSample.g.mul(0.25))
        .add(detailSample.b.mul(0.125));
      // Erode edges at cloud top more aggressively than at base — against the
      // PER-CLOUD height hn (each mass's own ceiling), and harder on tall
      // cumulus (billowy cauliflower tops) than on flat decks.
      const erodeAmt = mix(detailFBM, float(1).sub(detailFBM), clamp(hn.mul(2), float(0), float(1)));
      const erodeStrength = mix(float(0.14), float(0.3), weather.g);
      const eroded = clamp(preErode.sub(erodeAmt.mul(erodeStrength)), float(0), float(1));

      return eroded.mul(u.densityScale);
    };

    // TSL helper: single-g HG phase.
    const hg = (cosTheta: any, g: any): any => {
      const g2 = g.mul(g);
      const denom = pow(
        max(float(1).add(g2).sub(g.mul(cosTheta).mul(2)), float(1e-4)),
        float(1.5),
      );
      return float(1).sub(g2).div(denom).mul(1.0 / (4.0 * Math.PI));
    };

    // Dual-lobe HG.
    const dualHG = (cosTheta: any): any => {
      const f = hg(cosTheta, u.phaseForwardG);
      const b = hg(cosTheta, u.phaseBackwardG);
      return f.add(b).mul(0.5);
    };

    // TSL helper: sample sun optical depth via 4-sample cone.
    const sunCone = (p: any): any => {
      const L = normalize(u.sunDirection);
      const od = float(0).toVar('od');
      // 4 fixed offsets — small cone around L. Static offsets avoid noise in
      // sun-shadow sampling; production renderers use blue-noise rotation per
      // pixel but we skip that for v1 simplicity.
      const offsetLen = float(80); // cone step length (m)
      Loop({ start: uint(0), end: uint(sunSteps), type: 'uint' }, ({ i }) => {
        const t = offsetLen.mul(float(i).add(0.5));
        const sp = p.add(L.mul(t));
        const d = sampleDensity(sp);
        od.addAssign(d.mul(u.extinction).mul(offsetLen));
      });
      return od;
    };

    // Main compositor.
    const marchFn = Fn(() => {
      const uv = screenUV;
      // Reconstruct the world-space ray from UV using the EXPLICIT camera
      // uniforms (see CloudUniforms.cameraPos WHY — the ambient TSL camera
      // accessors are unreliable inside the post-quad render context).
      // screenUV is Y-DOWN (top-left origin) on BOTH backends — the compiled
      // WebGL shader is `resolution.y - gl_FragCoord.y`, WebGPU fragCoord is
      // natively top-origin — so NDC y must be 1-2v, NOT 2v-1. The old 2v-1
      // MIRRORED the whole ray field vertically: screen-top pixels traced
      // horizon rays, so the "overhead cloud band" was really the 10-40 km
      // horizon wall (translation-invariant ⇒ "clouds follow the camera") and
      // the zenith rendered at the horizon line. Proven by the uniform-
      // overcast bed test: the miss-the-slab black region sat at the TOP of
      // frame and grew as pitch DECREASED — exactly the mirror prediction.
      const ndc = vec4(uv.x.mul(2).sub(1), float(1).sub(uv.y.mul(2)), float(1), float(1));
      const viewH = u.cameraProjInvMat.mul(ndc);
      const viewDir4 = vec4(viewH.xyz, float(0));
      const worldDir4 = u.cameraWorldMat.mul(viewDir4);
      const rd = normalize(worldDir4.xyz);

      const ro = u.cameraPos;

      // Ray-slab intersection with the cloud layer (Y-bounded slab).
      // Guard against horizon-grazing rays (rd.y ≈ 0) that produce NaN/Inf
      // via 1/rd.y. `rd.y.sign()` returns 0 when rd.y is exactly 0 (GLSL/
      // WGSL semantics), which would zero out the entire safeRdY expression.
      // Use step-based "non-zero sign" instead — same fix pattern applied
      // to parallaxCorrection.ts. The `miss` check below still catches the
      // exactly-zero case, but TSL `.toVar()` storage can evaluate Inf
      // arithmetic before the branch gate, producing undefined behavior.
      const nonZeroSignY = step(float(0), rd.y).mul(float(2)).sub(float(1));
      const safeRdY = nonZeroSignY.mul(rd.y.abs().max(float(1e-5)));
      const invY = float(1).div(safeRdY);
      const tBottom = u.bottomAltitude.sub(ro.y).mul(invY);
      const tTop = u.topAltitude.sub(ro.y).mul(invY);
      const tNear = min(tBottom, tTop);
      const tFar = max(tBottom, tTop);
      const tStart = max(tNear, float(0));
      const tEnd = tFar;

      // Skip if camera is above or below with ray leading away, the ray is
      // grazing the horizon, or the sky is effectively CLEAR — a coverage < 2%
      // knob can never produce visible density, so skip the whole march (the
      // old code still paid the full step count of texture fetches per sky
      // pixel on cloudless days).
      const miss = tEnd.lessThanEqual(tStart)
        .or(rd.y.abs().lessThan(float(1e-5)))
        .or(u.coverage.lessThan(float(0.02)));

      const scat = vec3(0).toVar('scat');
      const trans = float(1).toVar('trans');

      If(miss.not(), () => {
        const cosTheta = dot(rd, normalize(u.sunDirection));
        const phase = dualHG(cosTheta);

        // Jittered march start — a per-pixel offset de-correlates the step
        // positions between neighboring rays, converting slab banding into
        // unstructured noise (lets a given step count look one preset
        // higher). BLUE noise when the global LUT exists (CloudModule inits
        // it): white noise clusters error at low frequencies — visible
        // speckle at cloud edges ("grainy" field report) — while blue noise
        // pushes the same error into high frequencies SMAA and the eye
        // filter out. Sampled at exact 1:1 pixel:texel (NearestFilter +
        // RepeatWrapping) — stretching the LUT destroys its spectrum. The
        // hash fallback keeps bed harnesses / tests that never call
        // initBlueNoise() rendering identically to before.
        const bnTex = getBlueNoiseTexture();
        const jitter = bnTex
          ? texture(bnTex, screenCoordinate.xy.div(float(bnTex.image.width))).r
          : fract(sin(dot(screenUV, vec2(12.9898, 78.233))).mul(43758.5453));
        // Scale the jitter to the distance-grown IN-CLOUD step (dtIn), NOT the
        // empty-space step (dtOut): a fixed-size jitter stops masking banding
        // once the grown step exceeds it on far horizon paths, but dtOut is
        // 4-6x dtIn and spraying march starts across a full dtOut (~600 m at
        // deck distances on medium) makes each thin erosion-wisp a coin-flip
        // per pixel — the wide "fried" speckle fringe on every cloud edge
        // (2026-08-19 field report). Empty-space skips contribute no visible
        // quantization (density 0 accumulates nothing), so the de-phasing that
        // matters is the in-cloud step; dtIn-grown keeps horizon-bank de-banding
        // while narrowing the speckle fringe ~4x.
        const entryStep = float(dtIn).mul(float(1).add(tStart.mul(float(0.0006))));
        const tCursor = tStart.add(jitter.mul(entryStep)).toVar('tCursor');
        // March from entry to exit, adaptive step.
        Loop({ start: uint(0), end: uint(steps), type: 'uint' }, ({ i: _i }) => {
          // If we've exited or transmittance is ~0, bail.
          If(tCursor.greaterThan(tEnd).or(trans.lessThan(float(0.02))), () => {
            // break via overwrite of cursor
            tCursor.assign(tEnd.add(float(1)));
          });

          If(tCursor.lessThan(tEnd.add(float(0.5))), () => {
            const p = ro.add(rd.mul(tCursor));
            const d = sampleDensity(p);

            // Adaptive step: small inside density, large outside — and GROWING
            // with distance (×(1 + 6e-4·t): ~2.5× at 2.5 km, ~13× at 20 km).
            // WHY: with constant steps the march budget died ~5 km out and
            // distant/horizon cloud banks were TRUNCATED along a razor-straight
            // screen-space edge; distance-scaled steps reach ~28 km on the same
            // step count, trading detail only where perspective hides it.
            // Floor at 1e-3 to prevent infinite raymarch loop when both dtOut/dtIn are
            // configured to 0 or density is exactly 0 (would otherwise stall tCursor).
            const distGrow = float(1).add(tCursor.mul(float(0.0006)));
            const stepLen = mix(float(dtOut), float(dtIn),
              clamp(d.mul(10), float(0), float(1))).mul(distGrow).max(float(1e-3));

            If(d.greaterThan(float(0.001)), () => {
              const sigmaE = d.mul(u.extinction);
              const stepOD = sigmaE.mul(stepLen);
              const stepT = exp(stepOD.negate());

              // Sun visibility: 4-sample cone toward the sun.
              const sunOD = sunCone(p);
              const sunT = exp(sunOD.negate());

              // Beer-Powder energy
              const beer = exp(stepOD.negate());
              const powder = float(1).sub(exp(stepOD.mul(2).negate()));
              const energy = mix(float(1), powder, u.powderStrength).mul(float(2).mul(beer));

              // Height-based ambient (top brighter).
              const hAbs = p.y.sub(u.bottomAltitude);
              const hSpan = u.topAltitude.sub(u.bottomAltitude);
              const h = clamp(hAbs.div(hSpan), float(0), float(1));
              const ambient = mix(u.ambientBottom, u.ambientTop, h);

              // Scattered light from this step. The Beer-Powder energy applies
              // to the SUN path only — ambient is the whole sky dome and must
              // not be attenuated by sun-shadow terms. WHY: multiplying ambient
              // by `energy` (which → 0 in optically thick regions) rendered
              // edge-on/horizon cloud banks pitch BLACK — a razor dark band
              // across the sky — instead of the ambient-grey a real overcast
              // bank settles to when the direct sun is fully self-shadowed.
              const sunContrib = u.sunColor.mul(u.sunIlluminance).mul(sunT).mul(phase);
              const scatter = sunContrib.mul(energy).add(vec3(ambient, ambient, ambient));
              scat.addAssign(scatter.mul(trans).mul(float(1).sub(stepT)));
              trans.mulAssign(stepT);
            });

            tCursor.addAssign(stepLen);
          });
        });
      });

      const alpha = float(1).sub(trans);
      return vec4(scat, alpha);
    });

    this._node = marchFn();
    return this._node;
  }

  /**
   * Drop the memoized node so the next `buildCompositeNode()` re-reads
   * `config.quality` — step counts are baked into the graph as constants,
   * so a quality-preset change requires a node rebuild (the post pipeline
   * that owns the composite must also rebuild to pick it up).
   */
  invalidateNode(): void {
    this._node = null;
  }

  dispose(): void {
    // WHY not disposing the noise here: the 3D noise + weather textures are
    // caller-supplied and SHARED (CloudModule feeds one bake to the high
    // deck, the low layer and the mesh field) — disposing one layer used to
    // blank the others. CloudModule.dispose() releases the shared set.
    this._node = null;
  }
}

/**
 * Front-to-back "over" composite of two cloud layers: `front` (the LOW,
 * nearer layer) over `back` (the high deck). Both are the vec4 nodes returned
 * by `CloudRenderer.buildCompositeNode()` — .rgb is PREMULTIPLIED in-scattered
 * radiance (the march accumulates `scatter·trans·(1−stepT)`), .a is opacity —
 * so this is standard premultiplied OVER:
 *   out.rgb = front.rgb + back.rgb·(1 − front.a)
 *   out.a   = front.a   + back.a  ·(1 − front.a)
 * The result plugs into the same pipeline composite (`scene·(1−a) + rgb`).
 */
export function combineCloudLayers(front: unknown, back: unknown): unknown {
  const f = vec4(front as never);
  const b = vec4(back as never);
  const k = float(1).sub(f.a);
  return vec4(f.rgb.add(b.rgb.mul(k)), f.a.add(b.a.mul(k)));
}

/** CPU mirror of `combineCloudLayers` (exported for tests). */
export function combineCloudLayersCpu(
  front: { r: number; g: number; b: number; a: number },
  back: { r: number; g: number; b: number; a: number },
): { r: number; g: number; b: number; a: number } {
  const k = 1 - front.a;
  return {
    r: front.r + back.r * k,
    g: front.g + back.g * k,
    b: front.b + back.b * k,
    a: front.a + back.a * k,
  };
}
